import { useState, useEffect, useCallback } from 'react'
import { useAppStore } from '../../store/useAppStore'
import './SearchBar.css'
import { Search, X } from 'lucide-react'

interface SearchBarProps {
  placeholder?: string
  onSearch?: (query: string) => void
  isFirstTabActive?: boolean
}

const SearchBar = ({
  placeholder = 'Поиск',
  onSearch,
  isFirstTabActive = false,
}: SearchBarProps) => {
  const { searchQuery, setSearchQuery } = useAppStore()
  const [localQuery, setLocalQuery] = useState(searchQuery)

  // Синхронизируем локальное состояние с глобальным
  useEffect(() => {
    setLocalQuery(searchQuery)
  }, [searchQuery])

  // Debounced поиск для оптимизации
  const debouncedSearch = useCallback(
    debounce((query: string) => {
      setSearchQuery(query)
      onSearch?.(query)
    }, 300),
    [setSearchQuery, onSearch]
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Убираем автоматическую отправку формы, поиск происходит автоматически
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setLocalQuery(value)

    // Прокрутка страницы вверх при начале поиска
    if (value.trim() && !searchQuery.trim()) {
      const scrollTop = isFirstTabActive ? 340 : 300
      window.scrollTo({ top: scrollTop, behavior: 'smooth' })
    }

    // Автоматический поиск при вводе с debounce
    debouncedSearch(value)
  }

  const clearSearch = () => {
    setLocalQuery('')
    setSearchQuery('')
    onSearch?.('')
  }

  return (
    <div className="search-bar">
      <form onSubmit={handleSubmit} className="search-form">
        <div className="search-input-container">
          <input
            type="text"
            value={localQuery}
            onChange={handleChange}
            placeholder={placeholder}
            className="search-input"
          />
          {localQuery && (
            <button
              type="button"
              onClick={clearSearch}
              className="clear-button"
              aria-label="Очистить поиск"
            >
              <X />
            </button>
          )}
          <div className="search-icon">
            <Search />
          </div>
        </div>
      </form>
    </div>
  )
}

// Debounce функция для оптимизации поиска
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: number
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait) as unknown as number
  }
}

export default SearchBar
